import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import PulseLayout from "./PulseLayout";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtNum, fmtPct } from "./theme";
import { SkeletonCard, SectionTitle } from "./components";
import { regionName, ORIGIN_NAMES_IT } from "./regionNames";
import { pulseGet } from "./api";
import { FC_STROKE } from "./forecast";
import { useDarkMode } from "../hooks/useDarkMode";

const bdLabel = (bd, lang) => {
  if (!bd) return "";
  if (bd.name === "region_visited") return regionName(bd.code, lang, bd.label);
  return lang === "it" ? (ORIGIN_NAMES_IT[bd.code] || bd.label) : bd.label;
};

const Stat = ({ label, value, dm, testid }) => {
  const T = tokens(dm);
  return (
    <div className="p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: T.muted }}>{label}</p>
      <p className="pulse-num text-2xl font-semibold mt-1">{value}</p>
    </div>
  );
};

/** M4 — «Previsto vs osservato»: every forecast period observed since, with the run that made it. */
export default function ForecastAccuracyPage() {
  const { t, lang } = usePulseLocale();
  const [dm] = useDarkMode();
  const T = tokens(dm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let on = true;
    setLoading(true);
    pulseGet("forecast/evaluations", { lang })
      .then((d) => { if (on) setData(d); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [lang]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    return filter === "outside" ? all.filter((r) => !r.inside_80) : all;
  }, [data, filter]);
  const s = data?.summary;

  return (
    <PulseLayout active="italy">
      <div className="pulse-fade-up" data-testid="forecast-accuracy-page">
        <h1 className="pulse-serif text-4xl sm:text-5xl font-medium mb-2" data-testid="forecast-accuracy-title">{t("fa_title")}</h1>
        <p className="text-sm mb-6 max-w-3xl" style={{ color: T.muted }}>{t("fa_sub")}</p>
        {loading && <SkeletonCard h={240} dm={dm} />}
        {!loading && data && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
              <Stat dm={dm} label={t("fa_stat_evaluations")} value={s.evaluations} testid="fa-stat-evaluations" />
              <Stat dm={dm} label={t("fa_stat_coverage")} value={s.coverage80_pct === null ? "—" : `${fmtNum(s.coverage80_pct, lang, 1)}%`} testid="fa-stat-coverage" />
              <Stat dm={dm} label={t("fa_stat_median_err")} value={s.median_abs_pct_err === null ? "—" : `${fmtNum(s.median_abs_pct_err, lang, 1)}%`} testid="fa-stat-median-err" />
              <Stat dm={dm} label={t("fa_stat_run")} value={data.run ? `v${data.run.warehouse_version}` : "—"} testid="fa-stat-run" />
            </div>
            <SectionTitle dm={dm} right={
              <div className="flex items-center gap-1" role="tablist">
                {["all", "outside"].map((f) => (
                  <button key={f} role="tab" aria-selected={filter === f} onClick={() => setFilter(f)} data-testid={`fa-filter-${f}`}
                    className="text-xs font-bold uppercase tracking-wider px-3 py-1.5"
                    style={{ background: filter === f ? T.brand : T.surface, color: filter === f ? "#fff" : T.muted,
                      border: `1px solid ${filter === f ? T.brand : T.border}`, borderRadius: 2 }}>
                    {t(`fa_filter_${f}`)}
                  </button>
                ))}
              </div>}>
              {t("fa_table_title")}
            </SectionTitle>
            {!rows.length && (
              <div className="p-6 text-sm" style={{ background: T.surface, border: `1px solid ${T.border}`, color: T.muted }} data-testid="fa-empty">
                {filter === "outside" && (data?.rows || []).length ? t("fa_empty_outside") : t("fa_empty")}
              </div>
            )}
            {rows.length > 0 && (
              <div className="overflow-x-auto" style={{ border: `1px solid ${T.border}` }}>
                <table className="w-full text-sm" data-testid="fa-table">
                  <thead>
                    <tr style={{ background: T.surface2 }}>
                      {["fa_col_series", "fa_col_period", "fa_col_q50", "fa_col_band", "fa_col_observed", "fa_col_err", "fa_col_inside", "fa_col_run"].map((k) => (
                        <th key={k} className="text-left text-[11px] font-semibold uppercase tracking-wider px-3 py-2 whitespace-nowrap" style={{ color: T.muted }}>{t(k)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={`${r.run_id}-${r.series_key}-${r.period}`} style={{ borderTop: `1px solid ${T.border}`, background: i % 2 ? T.surface : "transparent" }} data-testid={`fa-row-${i}`}>
                        <td className="px-3 py-2">
                          <span className="font-semibold">{r.indicator_label}</span>
                          <span style={{ color: T.muted }}>{r.breakdown ? ` · ${bdLabel(r.breakdown, lang)}` : ""} · {r.ref_area}</span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{r.period_label}</td>
                        <td className="px-3 py-2 pulse-num" style={{ color: FC_STROKE }}>{fmtCompact(r.q50, lang)}</td>
                        <td className="px-3 py-2 pulse-num whitespace-nowrap" style={{ color: T.muted }}>{fmtCompact(r.q10, lang)} – {fmtCompact(r.q90, lang)}</td>
                        <td className="px-3 py-2 pulse-num font-semibold">{fmtCompact(r.observed, lang)}</td>
                        <td className="px-3 py-2 pulse-num">{fmtPct(r.pct_err, lang)}</td>
                        <td className="px-3 py-2">
                          {r.inside_80
                            ? <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: T.accent }}><CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />{t("fa_yes")}</span>
                            : <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: "#B42318" }}><XCircle className="w-3.5 h-3.5" aria-hidden="true" />{t("fa_no")} · {r.side === "above_q90" ? t("fa_above") : t("fa_below")}</span>}
                        </td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap" style={{ color: T.muted }} title={r.run_id}>{r.run_id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] mt-4" style={{ color: T.muted }}>
              {t("fa_note")} <Link to={`/pulse/italy${lang === "it" ? "?lang=it" : ""}`} className="underline" style={{ color: T.accent }}>{t("nav_italy")}</Link>
            </p>
          </>
        )}
      </div>
    </PulseLayout>
  );
}
